/**
 * Google Analytics 4 (GA4) service
 * Provides page view tracking, custom events, and user identification
 */

// Extend Window interface for gtag
declare global {
    interface Window {
        gtag?: (
            command: 'config' | 'event' | 'set' | 'js' | 'consent',
            targetId: string | Date | 'default' | 'update',
            config?: Record<string, any>
        ) => void;
        dataLayer?: any[];
    }
}

class AnalyticsService {
    private initialized = false;
    private measurementId: string | null = null;
    private hasAnalyticsConsent = false;
    private hasMarketingConsent = false;

    /**
     * Update consent mode based on user preferences
     * @param analyticsConsent - Whether user consented to analytics
     * @param marketingConsent - Whether user consented to marketing
     */
    updateConsent(analyticsConsent: boolean, marketingConsent: boolean): void {
        if (typeof window === 'undefined' || !window.gtag) {
            return;
        }

        this.hasAnalyticsConsent = analyticsConsent;
        this.hasMarketingConsent = marketingConsent;

        try {
            window.gtag('consent', 'update', {
                analytics_storage: analyticsConsent ? 'granted' : 'denied',
                ad_storage: marketingConsent ? 'granted' : 'denied'
            });

            // If consent is granted and not initialized, initialize now
            if (analyticsConsent && !this.initialized && this.measurementId) {
                this.initializeGA(this.measurementId);
            }
        } catch (error) {
            console.error('[AnalyticsService] Failed to update consent:', error);
        }
    }

    /**
     * Initialize Google Analytics
     * Call this once during app initialization
     * @param measurementId - Your GA4 Measurement ID (e.g., G-XXXXXXXXXX)
     */
    initialize(measurementId?: string): void {
        // Only initialize in browser environment
        if (typeof window === 'undefined') {
            return;
        }

        // Don't initialize if already initialized
        if (this.initialized) {
            console.warn('[AnalyticsService] Google Analytics is already initialized');
            return;
        }

        // Get measurement ID from parameter or environment variable
        const id = measurementId || import.meta.env.VITE_GA_MEASUREMENT_ID;

        if (!id) {
            if (import.meta.env.DEV) {
                console.warn(
                    '[AnalyticsService] Google Analytics Measurement ID not found. Set VITE_GA_MEASUREMENT_ID environment variable or pass measurementId to initialize()'
                );
            }
            return;
        }

        this.measurementId = id;

        // Initialize dataLayer if it doesn't exist
        if (!window.dataLayer) {
            window.dataLayer = [];
        }

        // Define gtag function if it doesn't exist
        if (!window.gtag) {
            window.gtag = function () {
                window.dataLayer!.push(arguments);
            };
        }

        // Only load GA script if we have consent
        // Otherwise, wait for consent update
        if (this.hasAnalyticsConsent) {
            this.initializeGA(id);
        } else {
            console.info('[AnalyticsService] Google Analytics will initialize after consent is granted');
        }
    }

    /**
     * Internal method to initialize Google Analytics script
     */
    private initializeGA(id: string): void {
        if (this.initialized || typeof window === 'undefined') {
            return;
        }

        // Load Google Analytics script with error handling
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
        
        // Handle script loading errors
        script.onerror = () => {
            // Silently handle script loading failures (e.g., network issues, ad blockers)
            if (import.meta.env.DEV) {
                console.warn('[AnalyticsService] Google Analytics script failed to load');
            }
        };

        script.onload = () => {
            try {
                // Configure Google Analytics
                window.gtag!('js', new Date());
                window.gtag!('config', id, {
                    send_page_view: false // We'll handle page views manually for SPA
                });

                this.initialized = true;
                console.info('[AnalyticsService] Google Analytics initialized successfully');
            } catch (error) {
                if (import.meta.env.DEV) {
                    console.warn('[AnalyticsService] Failed to configure Google Analytics:', error);
                }
            }
        };

        document.head.appendChild(script);
    }

    /**
     * Check if Google Analytics is initialized
     */
    isInitialized(): boolean {
        return this.initialized && typeof window !== 'undefined' && typeof window.gtag === 'function';
    }

    /**
     * Track a page view
     * @param path - The page path (e.g., '/workspace', '/simulations')
     * @param title - Optional page title
     */
    pageView(path: string, title?: string): void {
        if (!this.hasAnalyticsConsent || !this.isInitialized() || !this.measurementId) {
            return;
        }

        try {
            window.gtag!('config', this.measurementId, {
                page_path: path,
                page_title: title || document.title
            });
        } catch (error) {
            console.error('[AnalyticsService] Failed to track page view:', error);
        }
    }

    /**
     * Track a custom event
     * @param eventName - Name of the event (e.g., 'button_click', 'form_submit')
     * @param eventParams - Optional event parameters
     */
    event(eventName: string, eventParams?: Record<string, any>): void {
        if (!this.hasAnalyticsConsent || !this.isInitialized()) {
            return;
        }

        try {
            window.gtag!('event', eventName, eventParams || {});
        } catch (error) {
            console.error('[AnalyticsService] Failed to track event:', error);
        }
    }

    /**
     * Set user properties
     * @param properties - User properties to set
     */
    setUserProperties(properties: Record<string, any>): void {
        if (!this.hasAnalyticsConsent || !this.isInitialized() || !this.measurementId) {
            return;
        }

        try {
            window.gtag!('set', 'user_properties', properties);
        } catch (error) {
            console.error('[AnalyticsService] Failed to set user properties:', error);
        }
    }

    /**
     * Identify a user
     * @param userId - Unique user identifier
     * @param userProperties - Optional user properties
     */
    identify(userId: string, userProperties?: Record<string, any>): void {
        if (!this.hasAnalyticsConsent || !this.isInitialized() || !this.measurementId) {
            return;
        }

        try {
            window.gtag!('config', this.measurementId, {
                user_id: userId,
                ...(userProperties && { user_properties: userProperties })
            });
        } catch (error) {
            console.error('[AnalyticsService] Failed to identify user:', error);
        }
    }

    /**
     * Set custom dimensions or parameters
     * @param key - Parameter key
     * @param value - Parameter value
     */
    set(key: string, value: any): void {
        if (!this.isInitialized() || !this.measurementId) {
            return;
        }

        try {
            window.gtag!('set', key, value);
        } catch (error) {
            console.error('[AnalyticsService] Failed to set parameter:', error);
        }
    }
}

// Export singleton instance
export const analyticsService = new AnalyticsService();

